/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/ban-types */
/* Copyright (c) 2020-present CloudIO Inc */

import {
  DBRow,
  DataRecord,
  FieldType,
  NewRow,
} from '@cloudio-saas/datasource-types';
import { AttributeDataType } from 'componentregistry/components/SmartSearch';
import { HTMLMotionProps } from 'framer-motion';
import { ExpressionReturnType } from 'layout/Sidebar/panels/PropertiesView/property-editors/types';
import { AnalyzeFunction } from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';
import React, { CSSProperties, MutableRefObject, ReactNode } from 'react';
import { RGBColor } from 'react-color';
import { IconType } from '../icons/types';
import {
  BaseCommonProps,
  BaseTriggerProps,
  CommonProps,
} from '../layout/Sidebar/panels/PropertiesView/CommonProperties';
import type { DevProps } from '../platform/DevProps';
import type { Platform } from '../platform/Platform';
import type {
  ActionItemType,
  ActionItemTypeRegistry,
  AnyItem,
  ComponentItemType,
  ComponentItemTypeRegistry,
  IRegistry,
  ItemType,
  Page,
  TriggerItemType,
  TriggerItemTypeRegistry,
} from './ComponentRegistry';
import { gridSize, HeightType, themeColor } from './constants';

const DEFAULT_ID = 'DEFAULT_ID';
export type ItemID = string;
export type DomProps = {
  'data-testid'?: string;
  onContextMenu?: (e: React.MouseEvent<HTMLElement>) => void;
  onClickCapture?: () => void;
  onPointerDownCapture?: (e: React.PointerEvent) => void;
};
export interface BaseProps<K extends keyof ComponentItemTypeRegistry>
  extends Partial<
      Pick<
        HTMLMotionProps<'div'>,
        | 'initial'
        | 'transformTemplate'
        | 'layoutId'
        | 'onLayoutAnimationComplete'
      >
    >,
    Partial<DevProps>,
    Required<CommonProps> {
  appUid: string;
  children?: ReactNode;
  className?: string;
  domProps: DomProps;
  height?: number;
  index?: number;
  itemId: ItemID;
  itemType: K;
  onScreenIndex?: number;
  pageId: string;
  roleUid: string;
  platform: Platform;
  width?: number;
}

export interface ITriggerProps {
  appUid: string;
  roleUid: string;
  dsAlias?: string | undefined | null;
  itemId: ItemID;
  pageId: string;
  platform: Platform;
  recordId?: string | undefined | null;
  rowIndex?: number;
  triggerItemId: ItemID;
  disableProgressTracking?: boolean;
  records?: (DBRow<DataRecord> | NewRow<DataRecord>)[];
  embeddedPageInstId?: string | null;
}

export interface IActionProps<K extends keyof ActionItemTypeRegistry>
  extends ITriggerProps {
  itemType: K;
  nestedActionCounter: number;
}

export type ActionProps<K extends keyof ActionItemTypeRegistry> = Required<
  ActionItemTypeRegistry[K]
> &
  IActionProps<K>;

export type TriggerProps<K extends keyof TriggerItemTypeRegistry> =
  TriggerItemTypeRegistry[K] & ITriggerProps;

export interface Item<K extends ItemType> {
  children: ItemID[];
  itemId: ItemID;
  parent: ItemID;
  props: ItemData<K>;
  type: K;
}

export interface RequiredItem<K extends ItemType> {
  children: ItemID[];
  itemId: ItemID;
  parent: ItemID;
  props: RequiredItemData<K>;
  type: K;
}

export interface RuntimePage {
  [key: string]: ItemData<ItemType>;
}

export type ItemData<K extends ItemType> = IRegistry<K>;

export type ComponentItemData<K extends ComponentItemType> =
  ComponentItemTypeRegistry[K];

export type TriggerItemData<K extends TriggerItemType> =
  TriggerItemTypeRegistry[K];

export type RequiredTriggerItemData<K extends TriggerItemType> = Required<
  TriggerItemTypeRegistry[K]
>;

export type ActionItemData<K extends ActionItemType> =
  ActionItemTypeRegistry[K];

export type RequiredActionItemData<K extends ActionItemType> = Required<
  ActionItemTypeRegistry[K]
>;

export interface BaseItem {
  children: ItemID[];
  itemId: ItemID;
  parent: ItemID;
  props: BaseCommonProps;
  type: ItemType;
}

export type BaseActionItem = BaseTriggerItem;

export interface BaseTriggerItem extends BaseItem {
  props: BaseTriggerProps;
}

export interface BaseComponentItem extends BaseItem {
  props: CommonProps;
}

export type RequiredItemData<K extends ItemType> = Required<IRegistry<K>>;

export interface BaseProp {
  category?: string;
  helpText?: ((item: RequiredItem<never>) => string) | string;
  hidden?: boolean;
  label: string;
  preventMultiEdit?: boolean;
  required?:
    | ((item: RequiredItem<never>, context: PropertyContext) => boolean)
    | boolean;
  type: string;
}

export interface ItemIDProp extends BaseProp {
  initialFilter?: string;
  type: 'ItemID';
  validateItemId?: (value: string, context: PropertyContext) => string;
}

export interface MultiItemIDProp extends BaseProp {
  initialFilter?: string;
  type: 'MultiItemID';
  validateItemId?: (value: string, context: PropertyContext) => string;
}

export interface StringProp extends BaseProp {
  maxLength?: number;
  multiline?: boolean;
  placeholder?: string;
  type: 'string';
}

export interface OptionsProp extends BaseProp {
  placeholder?: string;
  type: 'options';
}

export interface ControllerFunctionProp extends BaseProp {
  alwaysShow?: boolean;
  type: 'controllerFunction';
}

export interface LookupCodeChooserProp extends BaseProp {
  type: 'LookupCodeChooser';
}

export interface LookupAttributeChooserProp extends BaseProp {
  type: 'LookupAttributeChooser';
}

export type ExpressionType = 'text' | 'expression';

export interface ExpressionValue {
  type: ExpressionType;
  value: string;
}

const DefaultExpressionValue: ExpressionValue = Object.freeze({
  type: 'text',
  value: '',
});

export interface LookupValueType {
  code: string;
  isNumeric: boolean;
}

export type ExpressionOnlyValue = string;

export interface BooleanExpressionBooleanValue {
  type: 'boolean';
  value: boolean;
}

export interface BooleanExpressionExpressionValue {
  type: 'expression';
  value: string;
}

export type BooleanExpressionValue =
  | BooleanExpressionBooleanValue
  | BooleanExpressionExpressionValue;

export interface IconValue {
  color?: string;
  iconName: string;
  iconType?: IconType;
}

export interface ExpressionProp extends BaseProp {
  type: 'expression';
  returnType: (
    item: RequiredItem<never>,
    context: PropertyContext,
  ) => Promise<ExpressionReturnType>;
  onAdd?: () => void;
  onRemove?: () => void;
  placeholder?: string;
  refreshCallback?: (callback: () => void) => void;
  hasTime?: boolean;
}

export interface ColorExpressionProp extends BaseProp {
  placeholder?: string;
  type: 'ColorExpression';
}

export interface ExpressionArrayProp extends BaseProp {
  type: 'expressionArray';
  returnType: (
    item: RequiredItem<never>,
    context: PropertyContext,
  ) => Promise<ExpressionReturnType>;
  placeholder?: string;
  refreshCallback?: (callback: () => void) => void;
}

export interface ExpressionOnlyProp extends BaseProp {
  type: 'expressionOnly';
  returnType: (
    item: RequiredItem<never>,
    context: PropertyContext,
  ) => 'string' | 'boolean' | 'number' | 'any' | 'array';
  placeholder?: string;
  refreshCallback?: (callback: () => void) => void;
}

export interface BooleanExpressionProp extends BaseProp {
  type: 'booleanExpression';
}

export interface NumberProp extends BaseProp {
  max?: number;
  min?: number;
  step?: number;
  hideInput?: boolean;
  type: 'number';
}

export interface NumberInputProp extends BaseProp {
  type: 'numberInput';
}

export interface BooleanProp extends BaseProp {
  type: 'boolean';
}

export interface ThemeBGColorProp extends BaseProp {
  filterColor?: (color: keyof typeof themeColor) => boolean;
  type: 'ThemeBGColor';
}

export interface BGColorProp extends BaseProp {
  allowCustomColor: boolean;
  type: 'BGColor';
}

export interface SelectProp extends BaseProp {
  type: 'select';
  options:
    | Record<string, string | number | boolean>
    | ((
        item: RequiredItem<never>,
        context: PropertyContext,
      ) => Promise<Record<string, string | number | boolean>>);
  optionsRefreshCallback?: (callback: () => void) => void;
  useDropDown?:
    | boolean
    | ((item: RequiredItem<never>, context: PropertyContext) => boolean);
}

export interface RadioProp extends BaseProp {
  type: 'radio';
  options:
    | Record<string | number, string | number | boolean>
    | ((
        item: RequiredItem<never>,
        context: PropertyContext,
      ) => Promise<Record<string | number, string | number | boolean>>);
}

export interface HeightProp extends BaseProp {
  type: 'height';
}

export type AliasType =
  | 'CALENDAR'
  | 'LOV'
  | 'QUERY_FILTER'
  | 'STORE'
  | 'TRANSACTION';

export type AliasTypeOrFn =
  | AliasType
  | ((context: PropertyContext) => AliasType);
export interface ViewAttributeChooserProp extends BaseProp {
  aliasType: AliasTypeOrFn;
  attributeRefreshCallback?: (callback: () => void) => void;
  getAttributeDataTypes: (props: RequiredItem<never>) => FieldType[] | 'ALL';
  type: 'ViewAttributeChooser';
  getAlias?: (
    page: Page,
    item: AnyItem,
    context: PropertyContext,
  ) => AliasAndEmbeddedPageIdValue;
}

export interface InvokeAttributeChooserProp extends BaseProp {
  aliasType: AliasTypeOrFn;
  attributeRefreshCallback?: (callback: () => void) => void;
  getAttributeDataTypes: (props: RequiredItem<never>) => FieldType[] | 'ALL';
  type: 'InvokeAttributeChooser';
  getAlias?: (
    page: Page,
    item: AnyItem,
    context: PropertyContext,
  ) => AliasAndEmbeddedPageIdValue;
}
export interface PageIDChooserProp extends BaseProp {
  type: 'PageIDChooser';
}

export interface ComboAttributeChooserProp extends BaseProp {
  aliasType: AliasTypeOrFn;
  attributeRefreshCallback?: (callback: () => void) => void;
  getAttributeDataTypes: (item: RequiredItem<never>) => FieldType[] | 'ALL';
  type: 'ComboAttributeChooser';
}

export interface ImgAttributeChooserProp extends BaseProp {
  type: 'ImgAttributeChooser';
}
export interface DataSourceChooserProp extends BaseProp {
  refreshCallback: (callback: () => void) => void;
  type: 'DataSourceChooser';
}

export interface AppUidChooserProp extends BaseProp {
  type: 'AppUidChooser';
}

export interface FunctionURIChooserProp extends BaseProp {
  refreshCallback: (callback: () => void) => void;
  type: 'FunctionURIChooser';
  getAppUid: (
    page: Page,
    item: AnyItem,
    context: PropertyContext,
  ) => string | undefined;
}

export interface DataSourceAliasProp extends BaseProp {
  type: 'DataSourceAlias';
}

export type DSType =
  | 'CloudDataStore'
  | 'DataStore'
  | 'LocalDataStore'
  | 'ReadOnlyDataStore'
  | 'TransientDataStore';

export interface DataSourceAliasChooserProp extends BaseProp {
  allowedTypes: DSType[];
  pageIdProp?: string;
  type: 'DataSourceAliasChooser';
}

export interface PropertyChooserProp extends BaseProp {
  itemIdProp: string;
  type: 'PropertyChooser';
}

export interface PropertyValueProp extends BaseProp {
  itemIdProp: string;
  propertyProp: string;
  type: 'PropertyValue';
}

export interface CSVProp extends BaseProp {
  type: 'csv';
}

export interface JSONProp extends BaseProp {
  type: 'json';
  sampleData?: string;
}

export interface TypescriptProp extends BaseProp {
  type: 'typescript';
  defaultScript: string;
  preCompile?: (script: string) => string;
  compileOnChange?: boolean;
}

export interface GridProp extends BaseProp {
  options: typeof gridSize;
  type: 'grid';
}

export interface PaddingProp extends BaseProp {
  type: 'padding';
}

export interface MarginProp extends BaseProp {
  type: 'margin';
}

export interface BorderProp extends BaseProp {
  type: 'border';
}

export interface IconSelectProp extends BaseProp {
  parentColorProp?: string;
  type: 'iconselect';
  useParentColor?: boolean;
}

export interface ResponsiveProp extends BaseProp {
  type: 'responsive';
}

export interface OperatorProp extends BaseProp {
  refreshCallback?: (callback: () => void) => void;
  type: 'operator';
  getDataType: (
    item: RequiredItem<never>,
    context: PropertyContext,
  ) => Promise<AttributeDataType>;
}
export interface MultiSelectOperatorProp extends BaseProp {
  refreshCallback?: (callback: () => void) => void;
  type: 'multiSelectOperator';
  getDataType: (
    item: RequiredItem<never>,
    context: PropertyContext,
  ) => Promise<AttributeDataType>;
}

export type ValueOf<T> = T[keyof T];

export interface Padding {
  all?: number;
  bottom?: number;
  left?: number;
  right?: number;
  top?: number;
}

export interface Margin {
  all?: number;
  bottom?: number;
  left?: number;
  right?: number;
  top?: number;
}

export interface BorderStyle {
  borderColor?: string | RGBColor;
  style?: string;
  width: number;
}
export interface Border {
  all?: BorderStyle;
  bottom?: BorderStyle;
  left?: BorderStyle;
  right?: BorderStyle;
  top?: BorderStyle;
}

export interface GridFlex {
  all?: ValueOf<typeof gridSize>;
  lg?: ValueOf<typeof gridSize>;
  md?: ValueOf<typeof gridSize>;
  sm?: ValueOf<typeof gridSize>;
  xl?: ValueOf<typeof gridSize>;
  xs?: ValueOf<typeof gridSize>;
}

export interface Height {
  type: HeightType;
  value?: number;
}

export type PropertyDefinition =
  | AppUidChooserProp
  | BGColorProp
  | BooleanExpressionProp
  | BooleanProp
  | BorderProp
  | CSVProp
  | ColorExpressionProp
  | ComboAttributeChooserProp
  | ControllerFunctionProp
  | DataSourceAliasChooserProp
  | DataSourceAliasProp
  | DataSourceChooserProp
  | ExpressionArrayProp
  | ExpressionOnlyProp
  | ExpressionProp
  | FunctionURIChooserProp
  | GridProp
  | HeightProp
  | IconSelectProp
  | ImgAttributeChooserProp
  | ItemIDProp
  | JSONProp
  | LookupCodeChooserProp
  | MarginProp
  | MultiItemIDProp
  | NumberInputProp
  | NumberProp
  | OperatorProp
  | OptionsProp
  | PaddingProp
  | PageIDChooserProp
  | PropertyChooserProp
  | PropertyValueProp
  | RadioProp
  | ResponsiveProp
  | SelectProp
  | StringProp
  | ThemeBGColorProp
  | TypescriptProp
  | ViewAttributeChooserProp
  | InvokeAttributeChooserProp
  | LookupAttributeChooserProp
  | MultiSelectOperatorProp;

export type PropertyType = PropertyDefinition['type'];

export type PropertyDefinitions<T> = Record<
  keyof Required<T>,
  PropertyDefinition
>;

export type Ref =
  | ((instance: unknown | null) => void)
  | MutableRefObject<unknown | null>
  | null;

export interface PropertyContext {
  appUid: string;
  itemId: string;
  mgr: Manager;
  pageId: string;
  platform: Platform;
}

export interface AliasAndEmbeddedPageIdProp {
  aliasProp: string;
  embeddedPageIdProp?: string;
}

export interface AliasAndEmbeddedPageIdValue {
  alias: string;
  embeddedPageId?: string;
}

export interface Manager {
  category?: string;
  defaultChildren: readonly ItemType[];
  defaultValue: Record<string, unknown>;
  description?: string;
  icon: IconValue | ((item: Item<any>) => IconValue);
  id?: string;
  managerType: 'Trigger' | 'Action' | 'Component';
  name: string;
  onReplace: (item: Item<any>, fromItem: Item<any>, page: Page) => Item<any>;
  platform: Platform;
  replaceableWith:
    | ((item: AnyItem) => readonly ItemType[])
    | readonly ItemType[];
  type: ItemType;
  version?: string;
  isChildAllowed: (
    page: Page,
    parentItem: AnyItem,
    child: ItemType,
    context: PropertyContext,
  ) => boolean | string;
  isAllowedInsideParent: (
    page: Page,
    parentItem: AnyItem,
    context: PropertyContext,
  ) => boolean | string;
  props: PropertyDefinitions<unknown>;
  getDataSourceAliasProperty: (
    type: AliasType,
    context: PropertyContext,
  ) => AliasAndEmbeddedPageIdProp | undefined;
  isDataSourceAliasUsed: (item: Item<any>, alias: string) => boolean;
  isItemIdUsed: (item: Item<any>, alias: string) => boolean;
  getDataSourceProperty: (
    itemData: Item<any>,
    type: AliasType,
  ) => string | undefined;
  validateProperty(
    property: unknown,
    value: unknown,
    itemProps: any,
  ): string | null;
  onPropertyChange(
    property: unknown,
    value: unknown,
    itemProps: RequiredItemData<never>,
    context: PropertyContext,
  ): Record<string, unknown>;
  isPropertyVisible(
    property: unknown,
    itemProps: RequiredItemData<never>,
    context: PropertyContext,
  ): boolean;
  getDisplayText: (
    item: RequiredItem<never>,
    expanded: boolean,
    context: PropertyContext,
  ) => string;
  updateMetaData: (
    props: Item<any>,
    data: ItemData<any>,
  ) => Required<ItemData<any>>;
  onChildMoveIn: (props: {
    child: AnyItem;
    oldValue: Item<any>;
    page: Readonly<Page>;
    appUid: string;
    pageId: ItemID;
    value: Item<any>;
  }) => void;
  onChildMoveOut: (props: {
    child: AnyItem;
    oldValue: Item<any>;
    page: Readonly<Page>;
    appUid: string;
    pageId: ItemID;
    value: Item<any>;
  }) => void;
  onMove: (props: {
    appUid: string;
    pageId: ItemID;
    oldValue: Item<any>;
    value: Item<any>;
  }) => Item<any>;
  analyze?: AnalyzeFunction;
  canHaveChildren: () => boolean;
  deprecated?: string;
  fixDeprecated?: (item: Item<any>, context: PropertyContext) => void;
  helpRenders: readonly ComponentSample<any>[];
  hideInComponentsList?: boolean; // hides the component from showing up in the components sidebar tab and help docs.
  isItemIdReadOnly: () => boolean;
  onCopy: (value: ItemData<any>, samePage: boolean) => ItemData<any>;
  generateShortId: (item: ItemData<any>) => string | undefined;
}

interface BaseActionManager extends Manager {
  action?: (props: ActionProps<never>) => Promise<boolean>;
  managerType: 'Action';
}

export interface ActionManager<
  K extends keyof ActionItemTypeRegistry = keyof ActionItemTypeRegistry,
> extends BaseActionManager {
  action?: (props: ActionProps<K>) => Promise<boolean>;
  platform: Platform;
  defaultValue: Required<ActionItemTypeRegistry[K]>;
  defaultChildren: readonly ItemType[];
  helpRenders: readonly ComponentSample<K>[];
  props: PropertyDefinitions<
    Omit<ActionItemTypeRegistry[K], keyof CommonProps>
  >;
  type: K;
  getDataSourceProperty: (
    itemData: RequiredItem<K>,
    type: AliasType,
  ) => string | undefined;
  validateProperty<KK extends keyof ActionItemTypeRegistry[K]>(
    property: KK,
    value: ActionItemTypeRegistry[K][KK],
    itemProps: RequiredActionItemData<K>,
  ): string | null;
  onPropertyChange<KK extends keyof ActionItemTypeRegistry[K]>(
    property: KK,
    value: ActionItemTypeRegistry[K][KK],
    itemProps: RequiredActionItemData<K>,
    context: PropertyContext,
  ): Partial<ActionItemTypeRegistry[K]>;
  isPropertyVisible<KK extends keyof ActionItemTypeRegistry[K]>(
    property: KK,
    itemProps: RequiredActionItemData<K>,
    context: PropertyContext,
  ): boolean;
  getDisplayText: (item: RequiredItem<K>, expanded: boolean) => string;
  updateMetaData: (
    props: Item<K>,
    data: RequiredActionItemData<K>,
  ) => Required<ActionItemTypeRegistry[K]>;
  onChildMoveIn: (props: {
    child: AnyItem;
    oldValue: Item<K>;
    page: Readonly<Page>;
    appUid: string;
    pageId: ItemID;
    value: Item<K>;
  }) => void;
  onChildMoveOut: (props: {
    child: AnyItem;
    oldValue: Item<K>;
    page: Readonly<Page>;
    pageId: ItemID;
    appUid: string;
    value: Item<K>;
  }) => void;
  onMove: (props: {
    appUid: string;
    pageId: ItemID;
    oldValue: Item<K>;
    value: Item<K>;
  }) => Item<K>;
  onCopy: (
    value: ActionItemTypeRegistry[K],
    samePage: boolean,
  ) => ActionItemTypeRegistry[K];
  isDataSourceAliasUsed: (item: Item<K>, alias: string) => boolean;
  isItemIdUsed: (item: Item<K>, alias: string) => boolean;
}

interface BaseTriggerManager extends Manager {
  trigger?: (props: TriggerProps<any>) => Promise<boolean>;
  managerType: 'Trigger';
}

export interface TriggerManager<
  K extends keyof TriggerItemTypeRegistry = keyof TriggerItemTypeRegistry,
> extends BaseTriggerManager {
  trigger?: (props: TriggerProps<K>) => Promise<boolean>;
  defaultValue: Required<TriggerItemTypeRegistry[K]>;
  props: PropertyDefinitions<
    Omit<TriggerItemTypeRegistry[K], keyof CommonProps>
  >;
  type: K;
  getDataSourceProperty: (
    item: RequiredItem<K>,
    type: AliasType,
  ) => string | undefined;
  validateProperty<KK extends keyof TriggerItemTypeRegistry[K]>(
    property: KK,
    value: TriggerItemTypeRegistry[K][KK],
    itemProps: RequiredTriggerItemData<K>,
  ): string | null;
  onPropertyChange<KK extends keyof TriggerItemTypeRegistry[K]>(
    property: KK,
    value: TriggerItemTypeRegistry[K][KK],
    itemProps: RequiredTriggerItemData<K>,
    context: PropertyContext,
  ): Partial<TriggerItemTypeRegistry[K]>;
  isPropertyVisible<KK extends keyof TriggerItemTypeRegistry[K]>(
    property: KK,
    itemProps: RequiredTriggerItemData<K>,
    context: PropertyContext,
  ): boolean;
  getDisplayText: (item: RequiredItem<K>, expanded: boolean) => string;
  updateMetaData: (
    props: Item<K>,
    data: Required<TriggerItemTypeRegistry[K]>,
  ) => Required<TriggerItemTypeRegistry[K]>;
  onChildMoveIn: (props: {
    child: AnyItem;
    oldValue: Item<K>;
    page: Readonly<Page>;
    pageId: ItemID;
    value: Item<K>;
  }) => void;
  onChildMoveOut: (props: {
    child: AnyItem;
    oldValue: Item<K>;
    page: Readonly<Page>;
    pageId: ItemID;
    value: Item<K>;
  }) => void;
  onMove: (props: {
    pageId: ItemID;
    oldValue: Item<K>;
    value: Item<K>;
  }) => Item<K>;
  onCopy: (
    value: TriggerItemTypeRegistry[K],
    samePage: boolean,
  ) => TriggerItemTypeRegistry[K];
}

interface BaseComponentManager extends Manager {
  managerType: 'Component';
  layout: 'auto' | 'sizeAware' | 'fixed';
  Component?: React.FunctionComponent<any>;
  DEVComponent?: React.FunctionComponent<any>;
  devStyles?: CSSProperties;
  excludeChildren: boolean; // set it to true when the component manages the rendering of it's children e.g. DataGrid, TabItem
  allowClick: boolean; // set it to true to allow clicking in designer mode e.g. TabItem
  category: string;
  noOverlay?: boolean; // set to true for components like Menu, MenuItem, Popup for which there is no need to add an overlay or wrap with motion
  getWrapperProps: (props: {
    item: Item<any>;
    platform: Platform;
    pageId: ItemID;
    appUid: string;
  }) => unknown;
  showEmptyPlaceholderInDesignerView: boolean; // set it to true to hide the empty placeholder. used by card header & menu
  getParentItemTypes: () => readonly ItemType[];
  appendOverlay?: boolean;
  prependOverlay?: boolean;
  overlayZIndex?: number;
  helpRenders: readonly ComponentSample<any>[];
}

export interface ComponentSample<T extends ItemType> {
  description?: string;
  exampleId: string;
  page: Page;
  controller?: string;
  item: Item<T>;
  title: string;
  hideProps?: boolean;
  visibleProps?: (keyof ItemData<T>)[];
  clearParentChildren?: boolean;
  height?: number;
}

export type AnyManager =
  | BaseComponentManager
  | BaseActionManager
  | BaseTriggerManager;

export interface ComponentManager<K extends ComponentItemType, R = never>
  extends BaseComponentManager {
  defaultValue: RequiredItemData<K>;
  defaultChildren: readonly ItemType[];
  props: PropertyDefinitions<Omit<ComponentItemData<K>, keyof CommonProps>>;
  helpRenders: readonly ComponentSample<K>[];
  type: K;
  icon: IconValue | ((item: Item<K>) => IconValue);
  onReplace: (item: Item<K>, fromItem: Item<any>, page: Page) => Item<K>;
  getDataSourceProperty: (item: Item<K>, type: AliasType) => string | undefined;
  validateProperty<KK extends keyof ComponentItemData<K>>(
    property: KK,
    value: ComponentItemData<K>[KK],
    itemProps: RequiredItemData<K>,
  ): string | null;
  onPropertyChange<KK extends keyof ComponentItemData<K>>(
    property: KK,
    value: ComponentItemData<K>[KK],
    itemProps: RequiredItemData<K>,
    context: PropertyContext,
  ): Partial<ComponentItemData<K>>;
  isPropertyVisible<KK extends keyof ComponentItemData<K>>(
    property: KK,
    itemProps: RequiredItemData<K>,
    context: PropertyContext,
  ): boolean;
  getDisplayText: (
    item: RequiredItem<K>,
    expanded: boolean,
    context: PropertyContext,
  ) => string;
  updateMetaData: (
    props: BaseComponentItem,
    data: RequiredItemData<K>,
  ) => RequiredItemData<K>;
  getWrapperProps: (props: {
    item: Item<K>;
    platform: Platform;
    pageId: ItemID;
    appUid: string;
  }) => R;
  onChildMoveIn: (props: {
    child: AnyItem;
    oldValue: Item<K>;
    page: Readonly<Page>;
    appUid: string;
    pageId: ItemID;
    value: Item<K>;
  }) => void;
  onChildMoveOut: (props: {
    child: AnyItem;
    oldValue: Item<K>;
    page: Readonly<Page>;
    appUid: string;
    pageId: ItemID;
    value: Item<K>;
  }) => void;
  onMove: (props: {
    appUid: string;
    pageId: ItemID;
    oldValue: Item<K>;
    value: Item<K>;
  }) => Item<K>;
  onCopy: (
    value: ComponentItemData<K>,
    samePage: boolean,
  ) => ComponentItemData<K>;
  isDataSourceAliasUsed: (item: Item<K>, alias: string) => boolean;
  isItemIdUsed: (item: Item<K>, alias: string) => boolean;
  isList?: boolean; // if true the component is rendered multiple times based on the number of records in the store and hence things like cssClassName expression must not be evaluated outside the component
  generateShortId: (item: ItemData<K>) => string | undefined;
}

export type ComponentManagerFn<K extends ComponentItemType, R = never> = (
  platform: Platform,
  type: K,
) => ComponentManager<K, R>;

export type ActionManagerFn<K extends ActionItemType> = (
  platform: Platform,
) => ActionManager<K>;

export type TriggerManagerFn<K extends TriggerItemType> = (
  platform: Platform,
  type: K,
) => TriggerManager<K>;

export type AnyManagerFn =
  | ComponentManagerFn<any, any>
  | TriggerManagerFn<any>
  | ActionManagerFn<any>;

export interface DragProps extends BaseProps<never> {
  height?: number;
  type: ItemType;
  width: number;
}

export { DEFAULT_ID, DefaultExpressionValue };
